/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { forwardRef, useMemo, type ForwardedRef } from "react";
import { AsYouType } from "libphonenumber-js";

import type { TelColumn } from "../types";
import { Cell } from "./cells";
import { useTypo } from "../../typo";

const formatter = new AsYouType();

export const TelCell = forwardRef(
  <D extends Object>(
    {
      column,
      row,
      rowIndex,
      onPress,
      isHovered = false,
      isStickyFirstColumn = false,
      stickyFirstColumnShift = 0,
      index,
      tabIndex,
      updateFocusPointer,
      variant = "fill",
      position,
      isTopPinned = false,
      isBottomPinned = false,
      isLastRow = false,
      hasSelection,
      selectionOffset,
    }: {
      index: number;
      tabIndex?: number;
      column: TelColumn<D>;
      row: D;
      rowIndex: number;
      onPress?: () => void;
      isHovered?: boolean;
      isTopPinned?: boolean;
      isBottomPinned?: boolean;
      isStickyFirstColumn?: boolean;
      stickyFirstColumnShift?: number;
      updateFocusPointer: () => void;
      variant?: "fill" | "ghost";
      isLastRow?: boolean;
      hasSelection: boolean;
      selectionOffset: number;
      position: {
        firstLeft?: boolean;
        firstRight?: boolean;
        lastLeft?: boolean;
        lastRight?: boolean;
      };
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const typo = useTypo();

    const value = column.value(row, rowIndex);

    const phone = useMemo(() => {
      if (!value) return undefined;

      formatter.reset();
      formatter.input(value);

      return formatter.getNumber()?.formatInternational();
    }, [value]);

    return (
      <Cell
        ref={ref}
        index={index}
        tabIndex={tabIndex}
        onPress={onPress}
        isHovered={isHovered}
        isStickyFirstColumn={isStickyFirstColumn}
        stickyFirstColumnShift={stickyFirstColumnShift}
        updateFocusPointer={updateFocusPointer}
        variant={variant}
        align={"start"}
        position={position}
        isTopPinned={isTopPinned}
        isBottomPinned={isBottomPinned}
        isLastRow={isLastRow}
        hasSelection={hasSelection}
        selectionOffset={selectionOffset}
      >
        <span
          dir="ltr"
          css={{
            ...typo({
              level: "body2",
              density: "tight",
              weight: "regular",
              tabularNumbers: true,
            }),
          }}
        >
          {phone}
        </span>
      </Cell>
    );
  },
);
